import { ref } from 'vue';

export interface ToolUse {
  id: string;
  name: string;
  status: 'running' | 'done' | 'error';
}

export interface FlashcardPreview {
  id: string;
  shortId: string;
  question: string;
  answer: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  lessonId: string | null;
  isStreaming?: boolean;
  toolUses?: ToolUse[];
  flashcards?: FlashcardPreview[];
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export function useTutor() {
  const messages = ref<ChatMessage[]>([]);
  const isStreaming = ref(false);
  const error = ref<string | null>(null);
  const sessionId = ref<string | null>(null);
  const isLoadingSession = ref(false);
  const currentLessonId = ref<string | null>(null);
  const lessonCompleted = ref(false);
  const lessonAdvanced = ref<{ lessonId: string; lessonTitle: string } | null>(null);
  const lessonSkipChange = ref<{ lessonId: string; skipped: boolean; at: number } | null>(null);
  const curriculumCompleted = ref(false);

  let pendingText = '';
  let streamDone = false;
  let rafId: number | null = null;

  function revealRate(depth: number, done: boolean): number {
    let n = 1;
    if (depth > 200) n = 10;
    else if (depth > 100) n = 5;
    else if (depth > 50) n = 3;
    else if (depth > 20) n = 2;
    if (done) n = Math.max(n, Math.ceil(depth / 30));
    return n;
  }

  function tick() {
    rafId = null;
    if (pendingText.length === 0) {
      if (!streamDone) rafId = requestAnimationFrame(tick);
      return;
    }
    const msg = messages.value[messages.value.length - 1];
    if (!msg || msg.role !== 'assistant') {
      rafId = requestAnimationFrame(tick);
      return;
    }
    const n = revealRate(pendingText.length, streamDone);
    msg.content += pendingText.slice(0, n);
    pendingText = pendingText.slice(n);
    rafId = requestAnimationFrame(tick);
  }

  function ensureTicking() {
    if (rafId === null) rafId = requestAnimationFrame(tick);
  }

  function stopTicking() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function flushPendingTo(msg: ChatMessage | undefined) {
    if (msg && msg.role === 'assistant' && pendingText.length > 0) {
      msg.content += pendingText;
    }
    pendingText = '';
  }

  async function waitForDrain() {
    while (pendingText.length > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 16));
    }
  }

  async function createSession(curriculumId: string): Promise<string> {
    const response = await fetch(`${API_URL}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type: 'tutor', curriculumId }),
    });

    if (!response.ok) {
      throw new Error(response.status === 401 ? 'Not authenticated' : `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.id;
  }

  async function initCurriculumSession(curriculumId: string): Promise<void> {
    isLoadingSession.value = true;
    try {
      const response = await fetch(`${API_URL}/api/chat/sessions/find-or-create-tutor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ curriculumId }),
      });
      if (!response.ok) {
        throw new Error(response.status === 401 ? 'Not authenticated' : `HTTP ${response.status}`);
      }
      const { id } = (await response.json()) as { id: string };
      await loadSession(id);
    } finally {
      isLoadingSession.value = false;
    }
  }

  async function loadSession(id: string): Promise<void> {
    isLoadingSession.value = true;
    try {
      const response = await fetch(`${API_URL}/api/chat/sessions/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) return;
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      sessionId.value = id;
      messages.value = data.messages.map(
        (m: { role: string; content: string; lessonId: string | null }) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
          lessonId: m.lessonId ?? null,
        })
      );
    } finally {
      isLoadingSession.value = false;
    }
  }

  async function ensureSession(curriculumId: string): Promise<string> {
    if (sessionId.value) return sessionId.value;
    const id = await createSession(curriculumId);
    sessionId.value = id;
    return id;
  }

  async function sendMessage(
    userMessage: string,
    curriculumId: string,
    lessonId: string
  ) {
    if (!userMessage.trim() || isStreaming.value) return;

    error.value = null;
    currentLessonId.value = lessonId;

    try {
      const sid = await ensureSession(curriculumId);

      messages.value.push({ role: 'user', content: userMessage, lessonId });
      isStreaming.value = true;

      let activeLessonId = lessonId;
      messages.value.push({
        role: 'assistant',
        content: '',
        lessonId: activeLessonId,
        isStreaming: true,
        toolUses: [],
        flashcards: [],
      });
      let assistantMsg = messages.value[messages.value.length - 1]!;
      let toolUseEnded = false;
      pendingText = '';
      streamDone = false;
      ensureTicking();

      const response = await fetch(`${API_URL}/api/chat/tutor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: sid,
          curriculumId,
          lessonId,
          message: { role: 'user', content: userMessage },
        }),
      });

      if (!response.ok) {
        throw new Error(response.status === 401 ? 'Not authenticated' : `HTTP ${response.status}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      let done = false;
      while (!done) {
        const result = await reader.read();
        if (result.done) {
          done = true;
          break;
        }

        buffer += decoder.decode(result.value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ') && currentEvent) {
            const data = JSON.parse(line.slice(6));
            switch (currentEvent) {
              case 'text_delta':
                if (toolUseEnded && (assistantMsg.content || pendingText.length > 0)) {
                  flushPendingTo(assistantMsg);
                  assistantMsg.isStreaming = false;
                  messages.value.push({
                    role: 'assistant',
                    content: '',
                    lessonId: activeLessonId,
                    isStreaming: true,
                    toolUses: [],
                    flashcards: [],
                  });
                  assistantMsg = messages.value[messages.value.length - 1]!;
                }
                toolUseEnded = false;
                pendingText += data.text;
                ensureTicking();
                break;
              case 'tool_use_start':
                if (!assistantMsg.toolUses) assistantMsg.toolUses = [];
                assistantMsg.toolUses.push({
                  id: data.toolUseId,
                  name: data.name,
                  status: 'running',
                });
                break;
              case 'tool_use_end': {
                const tu = assistantMsg.toolUses?.find(
                  (t) => t.id === data.toolUseId
                );
                if (tu) tu.status = data.ok === false ? 'error' : 'done';
                toolUseEnded = true;
                break;
              }
              case 'lesson_completed':
                lessonCompleted.value = true;
                break;
              case 'curriculum_completed':
                curriculumCompleted.value = true;
                break;
              case 'lesson_advanced':
                lessonAdvanced.value = {
                  lessonId: data.lessonId,
                  lessonTitle: data.lessonTitle,
                };
                currentLessonId.value = data.lessonId;
                activeLessonId = data.lessonId;
                break;
              case 'lesson_skipped':
                lessonSkipChange.value = {
                  lessonId: data.lessonId,
                  skipped: true,
                  at: Date.now(),
                };
                break;
              case 'lesson_unskipped':
                lessonSkipChange.value = {
                  lessonId: data.lessonId,
                  skipped: false,
                  at: Date.now(),
                };
                break;
              case 'flashcard_created':
                if (!assistantMsg.flashcards) assistantMsg.flashcards = [];
                assistantMsg.flashcards.push({
                  id: data.flashcardId,
                  shortId: data.shortId,
                  question: data.question,
                  answer: data.answer,
                });
                break;
              case 'error':
                error.value = data.message;
                break;
            }
            currentEvent = '';
          }
        }
      }

      streamDone = true;
      await waitForDrain();
      assistantMsg.isStreaming = false;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Connection failed';
      const last = messages.value[messages.value.length - 1];
      flushPendingTo(last);
      if (last && last.role === 'assistant') last.isStreaming = false;
    } finally {
      streamDone = true;
      stopTicking();
      isStreaming.value = false;
    }
  }

  return {
    messages,
    isStreaming,
    isLoadingSession,
    error,
    sessionId,
    currentLessonId,
    lessonCompleted,
    lessonAdvanced,
    lessonSkipChange,
    curriculumCompleted,
    sendMessage,
    loadSession,
    initCurriculumSession,
  };
}
